import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { mspApi } from '../../services/api';
import './Msp.css';

const normalizeTenants = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.tenants)) return payload.tenants;
  return [];
};

const DOMAIN_PATTERN = /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/i;

function Tenants() {
  const [tenants, setTenants] = useState([]);
  const [status, setStatus] = useState('loading');

  const [search, setSearch] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'riskScore', direction: 'desc' });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedTenantIds, setSelectedTenantIds] = useState([]);
  const [bulkTags, setBulkTags] = useState('');
  const [bulkSchedule, setBulkSchedule] = useState('monthly');
  const [bulkStatus, setBulkStatus] = useState('idle');
  const [newTenantName, setNewTenantName] = useState('');
  const [newTenantDomain, setNewTenantDomain] = useState('');
  const [createStatus, setCreateStatus] = useState('idle');

  const getTenantTrend = (tenant) => {
    const trend = tenant.risk_trend || tenant.riskTrend || tenant.trend || tenant.risk_history || [];
    if (Array.isArray(trend)) return trend;
    return [];
  };

  const normalizeTrend = (trend) => {
    if (!Array.isArray(trend)) return [];
    return trend
      .map((point) => {
        if (typeof point === 'number') return point;
        if (typeof point?.value === 'number') return point.value;
        if (typeof point?.risk_score === 'number') return point.risk_score;
        return null;
      })
      .filter((value) => typeof value === 'number');
  };

  const getSortValue = (tenant, key) => {
    switch (key) {
      case 'riskScore':
        return tenant.risk_score || tenant.riskScore || 0;
      case 'lastScan':
        return new Date(tenant.last_scan_at || tenant.lastScan || 0).getTime();
      case 'openFindings':
        return tenant.open_high_findings || tenant.openHighFindings || 0;
      default:
        return 0;
    }
  };

  const getSeverityBand = (tenant) => {
    const severity = (tenant.last_scan_severity || tenant.lastScanSeverity || '').toLowerCase();
    if (severity) return severity;
    const score = tenant.risk_score || tenant.riskScore || 0;
    if (score >= 90) return 'critical';
    if (score >= 70) return 'high';
    if (score >= 40) return 'medium';
    return 'low';
  };

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'desc' };
    });
  };

  const loadTenants = () => {
    setStatus('loading');
    mspApi
      .listTenants()
      .then((response) => {
        setTenants(normalizeTenants(response.data));
        setStatus('ready');
      })
      .catch(() => {
        setStatus('error');
      });
  };

  useEffect(() => {
    let isMounted = true;
    mspApi
      .listTenants()
      .then((response) => {
        if (!isMounted) return;
        setTenants(normalizeTenants(response.data));
        setStatus('ready');
      })
      .catch(() => {
        if (isMounted) {
          setStatus('error');
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const filteredTenants = useMemo(() => {
    const term = search.toLowerCase();
    return tenants.filter((tenant) => {
      const name = (tenant.name || tenant.tenant_name || '').toLowerCase();
      const domain = (tenant.primary_domain || tenant.primaryDomain || '').toLowerCase();
      return name.includes(term) || domain.includes(term);
    });
  }, [tenants, search]);

  const sortedTenants = useMemo(() => {
    const sorted = [...filteredTenants];
    sorted.sort((a, b) => {
      const aValue = getSortValue(a, sortConfig.key);
      const bValue = getSortValue(b, sortConfig.key);
      if (aValue === bValue) return 0;
      if (sortConfig.direction === 'asc') return aValue > bValue ? 1 : -1;
      return aValue < bValue ? 1 : -1;
    });
    return sorted;
  }, [filteredTenants, sortConfig]);

  const totalPages = Math.max(1, Math.ceil(sortedTenants.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pagedTenants = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return sortedTenants.slice(start, start + pageSize);
  }, [currentPage, pageSize, sortedTenants]);

  useEffect(() => {
    setPage(1);
  }, [search, pageSize, sortConfig]);

  const toggleTenantSelection = (tenantId) => {
    if (!tenantId) return;
    setSelectedTenantIds((prev) =>
      prev.includes(tenantId) ? prev.filter((id) => id !== tenantId) : [...prev, tenantId]
    );
  };

  const toggleSelectAll = () => {
    const pageIds = pagedTenants.map((tenant) => tenant.id || tenant.tenant_id).filter(Boolean);
    const allSelected = pageIds.every((id) => selectedTenantIds.includes(id));
    setSelectedTenantIds((prev) => {
      if (allSelected) {
        return prev.filter((id) => !pageIds.includes(id));
      }
      return Array.from(new Set([...prev, ...pageIds]));
    });
  };

  const handleBulkApply = async () => {
    if (selectedTenantIds.length === 0) return;
    setBulkStatus('submitting');
    try {
      const tags = bulkTags
        ? bulkTags.split(',').map((tag) => tag.trim()).filter(Boolean)
        : [];
      await mspApi.bulkUpdateTenants({
        tenant_ids: selectedTenantIds,
        tags: tags.length > 0 ? tags : undefined,
        report_schedule: bulkSchedule,
      });
      setSelectedTenantIds([]);
      setBulkTags('');
      setBulkStatus('success');
      loadTenants();
    } catch (error) {
      console.error('Bulk update failed', error);
      setBulkStatus('error');
    }
  };

  const handleExportCsv = () => {
    const headers = ['Tenant', 'Primary Domain', 'Risk Score', 'Last Scan', 'Open High Findings'];
    const rows = filteredTenants.map((tenant) => [
      tenant.name || tenant.tenant_name || tenant.id || tenant.tenant_id || '',
      tenant.primary_domain || tenant.primaryDomain || '',
      tenant.risk_score || tenant.riskScore || '',
      tenant.last_scan_at || tenant.lastScan || '',
      tenant.open_high_findings || tenant.openHighFindings || 0,
    ]);
    const csvContent = [headers, ...rows]
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'tenants.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleDeleteTenant = async (tenantId, tenantName) => {
    if (!window.confirm(`Are you sure you want to delete "${tenantName}"? This will remove all associated scans, assets, and alerts. This action cannot be undone.`)) {
      return;
    }
    try {
      await mspApi.deleteTenant(tenantId);
      setSelectedTenantIds((prev) => prev.filter((id) => id !== tenantId));
      loadTenants();
    } catch (error) {
      console.error('Failed to delete tenant', error);
      alert('Failed to delete tenant. Please try again.');
    }
  };

  const handleCreateTenant = async (event) => {
    event.preventDefault();
    const name = newTenantName.trim();
    const domain = newTenantDomain
      .trim()
      .toLowerCase()
      .replace(/^https?:\/\//, '')
      .replace(/\/.*/, '');

    if (!name || !DOMAIN_PATTERN.test(domain)) {
      setCreateStatus('invalid');
      return;
    }

    setCreateStatus('submitting');
    try {
      await mspApi.createTenant({
        name,
        primary_domain: domain,
      });
      setNewTenantName('');
      setNewTenantDomain('');
      setCreateStatus('success');
      loadTenants();
    } catch (error) {
      console.error('Failed to create tenant', error);
      setCreateStatus('error');
    }
  };

  return (
    <div className="msp-page">
      <div className="msp-header">
        <div>
          <h1>Tenants</h1>
          <p className="msp-subtitle">Monitor assets, alerts, and report readiness</p>
        </div>
        <span className="msp-pill">{tenants.length} Active</span>
      </div>

      <div className="msp-toolbar">
        <input
          type="text"
          className="msp-input"
          placeholder="Search tenants or domains"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <form className="msp-inline-form" onSubmit={handleCreateTenant}>
          <input
            type="text"
            className="msp-input"
            placeholder="Tenant name"
            value={newTenantName}
            onChange={(event) => {
              setNewTenantName(event.target.value);
              if (createStatus !== 'idle') setCreateStatus('idle');
            }}
            required
          />
          <input
            type="text"
            className="msp-input"
            placeholder="Primary domain"
            value={newTenantDomain}
            onChange={(event) => {
              setNewTenantDomain(event.target.value);
              if (createStatus !== 'idle') setCreateStatus('idle');
            }}
            required
          />
          <button
            type="submit"
            className="btn btn-primary"
            disabled={
              createStatus === 'submitting' ||
              newTenantName.trim().length === 0 ||
              newTenantDomain.trim().length === 0
            }
          >
            {createStatus === 'submitting' ? 'Creating…' : 'Create Tenant'}
          </button>
        </form>
      </div>

      {createStatus === 'success' && (
        <div className="msp-inline-success">Tenant created successfully.</div>
      )}
      {createStatus === 'invalid' && (
        <div className="msp-inline-error">Enter a valid domain like company.com.</div>
      )}
      {createStatus === 'error' && (
        <div className="msp-empty">Unable to create tenant. Try again.</div>
      )}

      {status === 'loading' && <div className="msp-empty">Loading tenant list…</div>}
      {status === 'error' && (
        <div className="msp-empty">Tenant inventory unavailable. Try again later.</div>
      )}
      {status === 'ready' && filteredTenants.length === 0 && (
        <div className="msp-empty">No tenants match your search.</div>
      )}
      {status === 'ready' && filteredTenants.length > 0 && (
        <>
          <div className="msp-bulk-toolbar">
            <div className="msp-bulk-left">
              <span className="msp-pill">{selectedTenantIds.length} Selected</span>
              <input
                type="text"
                className="msp-input"
                placeholder="Bulk tags (comma separated)"
                value={bulkTags}
                onChange={(event) => setBulkTags(event.target.value)}
              />
              <select
                className="msp-input"
                value={bulkSchedule}
                onChange={(event) => setBulkSchedule(event.target.value)}
              >
                <option value="monthly">Monthly Schedule</option>
                <option value="quarterly">Quarterly Schedule</option>
              </select>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleBulkApply}
                disabled={bulkStatus === 'submitting' || selectedTenantIds.length === 0}
              >
                {bulkStatus === 'submitting' ? 'Applying…' : 'Apply Bulk Updates'}
              </button>
              {bulkStatus === 'success' && <span className="msp-inline-success">Bulk update applied.</span>}
              {bulkStatus === 'error' && <span className="msp-inline-error">Bulk update failed.</span>}
            </div>
            <button type="button" className="btn btn-secondary" onClick={handleExportCsv}>
              Export CSV
            </button>
          </div>
          <table className="msp-table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    aria-label="Select all tenants"
                    onChange={toggleSelectAll}
                    checked={
                      pagedTenants.length > 0 &&
                      pagedTenants.every((tenant) =>
                        selectedTenantIds.includes(tenant.id || tenant.tenant_id)
                      )
                    }
                  />
                </th>
                <th>Tenant</th>
                <th>Primary Domain</th>
                <th>Risk Trend</th>
                <th>
                  <button type="button" className="msp-sort-button" onClick={() => handleSort('riskScore')}>
                    Risk Score
                    <span className={`msp-sort-indicator ${sortConfig.key === 'riskScore' ? 'active' : ''}`}>
                      {sortConfig.key === 'riskScore' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : '↕'}
                    </span>
                  </button>
                </th>
                <th>
                  <button type="button" className="msp-sort-button" onClick={() => handleSort('lastScan')}>
                    Last Scan
                    <span className={`msp-sort-indicator ${sortConfig.key === 'lastScan' ? 'active' : ''}`}>
                      {sortConfig.key === 'lastScan' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : '↕'}
                    </span>
                  </button>
                </th>
                <th>
                  <button type="button" className="msp-sort-button" onClick={() => handleSort('openFindings')}>
                    Open High Findings
                    <span className={`msp-sort-indicator ${sortConfig.key === 'openFindings' ? 'active' : ''}`}>
                      {sortConfig.key === 'openFindings' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : '↕'}
                    </span>
                  </button>
                </th>
                <th>Status</th>
                <th>Scan Severity</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {pagedTenants.map((tenant) => {
                const tenantId = tenant.id || tenant.tenant_id;
                const tenantName = tenant.name || tenant.tenant_name || tenantId;
                const trendValues = normalizeTrend(getTenantTrend(tenant));
                const severityBand = getSeverityBand(tenant);
                const domainVerified = tenant.domain_verified ?? tenant.domainVerified ?? false;
                return (
                  <tr key={tenantId}>
                    <td>
                      <input
                        type="checkbox"
                        aria-label={`Select ${tenantName}`}
                        checked={selectedTenantIds.includes(tenantId)}
                        onChange={() => toggleTenantSelection(tenantId)}
                      />
                    </td>
                    <td>{tenantName}</td>
                    <td>{tenant.primary_domain || tenant.primaryDomain || '—'}</td>
                    <td>
                      {trendValues.length > 1 ? (
                        <span className="msp-sparkline">
                          <svg viewBox="0 0 100 30" aria-hidden="true">
                            <polyline
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              points={trendValues
                                .map((value, index) => {
                                  const x = (index / (trendValues.length - 1)) * 100;
                                  const max = Math.max(...trendValues);
                                  const min = Math.min(...trendValues);
                                  const range = max - min || 1;
                                  const y = 30 - ((value - min) / range) * 28 - 1;
                                  return `${x},${y}`;
                                })
                                .join(' ')}
                            />
                          </svg>
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td>{tenant.risk_score || tenant.riskScore || '—'}</td>
                    <td>{tenant.last_scan_at || tenant.lastScan || '—'}</td>
                    <td>{tenant.open_high_findings || tenant.openHighFindings || 0}</td>
                    <td>
                      <span className={`msp-badge ${domainVerified ? 'msp-badge-good' : 'msp-badge-muted'}`}>
                        {domainVerified ? 'Domain Verified' : 'Domain Pending'}
                      </span>
                    </td>
                    <td>
                      <span className={`msp-badge msp-badge-${severityBand}`}>{severityBand}</span>
                    </td>
                    <td>
                      <Link className="msp-link" to={`/msp/tenants/${tenantId}`}>
                        View Tenant
                      </Link>
                      <button
                        type="button"
                        className="btn btn-danger"
                        style={{ marginLeft: '8px', padding: '2px 8px', fontSize: '0.7rem' }}
                        onClick={() => handleDeleteTenant(tenantId, tenantName)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="msp-pagination">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            <span className="msp-pagination-status">
              Page {currentPage} of {totalPages}
            </span>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
            <select
              className="msp-input msp-page-size"
              value={pageSize}
              onChange={(event) => setPageSize(Number(event.target.value))}
            >
              {[5, 10, 20].map((size) => (
                <option key={size} value={size}>
                  {size} / page
                </option>
              ))}
            </select>
          </div>
        </>
      )}
    </div>
  );
}

export default Tenants;
